import { bindActionCreators, compose } from 'redux';
import { connect } from 'react-redux';
import { isEmpty, isNil, replace } from 'lodash/fp';
import { REFER_A_FRIEND_OFFER_ID } from '@ahmdigital/constants';
import { useEffect } from 'react';

import { RootState } from '../../../reducers';
import { TARGET_SEGMENT } from '../../../cms/constants';
import { URLS } from '../../../models/url/constants';
import config from '../../../config';
import constants from '../../../ahm-constants';
import deriveMetaDataFromOffer from '../utils/derive-meta-data-from-offer';
import getOfferBehaviours from './utils/get-offer-behaviours';
import Head from '../../../../utils/head';
import Header from './components/header';
import HeaderAlert from './components/header-alert';
import HowItWorks from '../../../components/how-it-works';
import NeedsProductDataLoader from '../../../components/needs-product-data-loader';
import ProductsSection from '../../../components/sections/products-section';
import RedirectAdapter from '../../../utils/redirect-adapter';
import ReferralActions from '../../../actions/referral';
import selectFilteredProductsByCustomer from '../../../selectors/select-filtered-products-by-customer';
import selectReferralCode from '../../../selectors/select-referral-code';
import TermsAndConditions from '../../../components/offers/terms-and-conditions';
import TermsSection from '../../../components/sections/terms-section';
import useGetAllPlaceholders from '../../../components/offers/logic/use-get-all-placeholders';
import useGetIsReferralCodeValid from './hooks/use-get-is-referral-code-valid';
import useGetOfferByIdentifierSlug from '../../../components/offers/logic/use-get-offer-by-identifier-slug';
import useScrollToHashElement from '../../../utils/use-scroll-to-hash-element';
import type { ProductType } from '../../../types/product';

const TERMS_ANCHOR_ID = 'terms';

type ReferAFriendProps = {
  actions: {
    referral: {
      setCode: (...args: unknown[]) => unknown;
      setTargetSegment: (...args: unknown[]) => unknown;
      setUrl: (...args: unknown[]) => unknown;
    };
  };
  cookieReferralCode?: string;
  location: {
    query: {
      code?: string;
    };
  };
  match: {
    params: {
      offerId: string;
    };
  };
  products?: ProductType[];
};

const ReferAFriend = ({ actions, cookieReferralCode, match, location, products }: ReferAFriendProps) => {
  const offerId = match?.params?.offerId;
  const referralCode = location?.query?.code || cookieReferralCode;
  const { loading: isLoadingOffer, offer } = useGetOfferByIdentifierSlug({ id: offerId });
  const { loading: isLoadingPlaceholders, placeholders } = useGetAllPlaceholders();
  const { loading: isLoadingReferralCodeValid, isReferralCodeValid } = useGetIsReferralCodeValid(referralCode);
  const alertLevel = isReferralCodeValid ? 'success' : 'danger';
  const isReferPage = offerId === REFER_A_FRIEND_OFFER_ID.REFER;
  const showProductSection = offerId === REFER_A_FRIEND_OFFER_ID.FRIEND && !!products?.length;
  const productSectionProps = {
    compareButtonText: 'Check out other cover options',
    compareButtonToLink: constants.URLS.SALES.REFINE_PRICE,
    data: {
      subtitle:
        'If you join ahm on hospital & extras you could get 6 weeks free and any 2 & 6 month waits waived on extras. Here are a few options to get you started, or if there’s something else you need check out the full range of hospital & extras products available.',
      title: 'Some hospital & extras options to get you started',
    },
    withBorder: true,
  };
  // The https:// shouldn't be visible to the user, so we’re grabbing only the relevant parts of the URL like this
  const { host, pathname } = new URL(`${config.get('sales:url')}${URLS.SALES.OFFER_FRIEND_SHORT}`);
  const referralUrl = isReferralCodeValid && isReferPage ? `${host}${pathname}/${referralCode}` : null;

  const isAllDataReady = !isEmpty(offer) && !isEmpty(placeholders);
  const isLoading = isLoadingOffer || isLoadingPlaceholders;

  const { showAlertOnSuccess, showAlertWhenNoReferralCode, showFindCover, showMemberLogin } =
    getOfferBehaviours(offerId);

  useEffect(() => {
    if (isReferralCodeValid) {
      actions.referral.setCode(referralCode);
      actions.referral.setTargetSegment(TARGET_SEGMENT.FRIEND_LOOKING_FOR_PRIVATE_HEALTH_INSURANCE);
      actions.referral.setUrl(referralUrl);
    }
  }, [actions.referral, isLoadingReferralCodeValid, isReferralCodeValid, referralCode, referralUrl]);

  useScrollToHashElement({ isReady: !isLoading && isAllDataReady && !isLoadingReferralCodeValid });

  if (isLoading && !isAllDataReady) {
    return null;
  }

  if (isNil(offer)) {
    return <RedirectAdapter to={constants.URLS.SALES.ERROR_404} />;
  }

  const isReferralCodeMissing = isEmpty(referralCode);
  // There a bit going on here...
  // - optionally hide the alert even if the code is valid
  // - optionally show the alert if no code has been included in url
  // - show the alert if we have a code and it is invalid
  const showAlert =
    !isLoadingReferralCodeValid &&
    ((isReferralCodeValid && showAlertOnSuccess) ||
      (isReferralCodeMissing && showAlertWhenNoReferralCode) ||
      (!isReferralCodeMissing && !isReferralCodeValid));

  const { description, socialImageSrc, title } = deriveMetaDataFromOffer(offer);

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta name="og:title" content={replace('%s', title, constants.PAGE_TITLE_TEMPLATE)} />
        <meta name="twitter:title" content={replace('%s', title, constants.PAGE_TITLE_TEMPLATE)} />
        {description && <meta name="description" content={description} />}
        {description && <meta name="og:description" content={description} />}
        {description && <meta name="twitter:description" content={description} />}
        {socialImageSrc && <meta name="og:image" content={socialImageSrc} />}
        {socialImageSrc && <meta name="twitter:image:src" content={socialImageSrc} />}
        {!isReferPage && <meta name="robots" content="noindex" />}
      </Head>
      <Header
        beforeContent={showAlert && <HeaderAlert alertLevel={alertLevel} isReferPage={isReferPage} />}
        showCodeCopy={isReferralCodeValid}
        showFindCover={showFindCover}
        offer={offer}
        showMemberLogin={showMemberLogin && !isReferralCodeValid}
      />
      {offer.howTo && <HowItWorks howTo={offer.howTo} />}
      {showProductSection && <ProductsSection {...productSectionProps} products={products} />}
      <TermsSection
        id={TERMS_ANCHOR_ID}
        termsComponent={<TermsAndConditions offer={offer} placeholders={placeholders} withSmallText />}
      />
    </>
  );
};

// @ts-expect-error - Automatic, Please fix when editing this file
const mapDispatchToProps = (dispatch) => ({
  actions: {
    referral: bindActionCreators(ReferralActions, dispatch),
  },
});

const mapStateToProps = (state: RootState, props: unknown) => ({
  cookieReferralCode: selectReferralCode(state),
  products: selectFilteredProductsByCustomer(state, props),
});

export default compose(NeedsProductDataLoader, connect(mapStateToProps, mapDispatchToProps))(ReferAFriend);

export { ReferAFriend as ReferAFriendForTest };
